import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import { Menu, X, Mail, Phone, Facebook, Twitter, Instagram, Music2 } from 'lucide-react';
import { SOCIAL_LINKS } from '../constants';
import Analytics from './Analytics';
import BottomNav from './BottomNav';

interface LayoutProps {
  children: React.ReactNode;
}

function Layout({ children }: LayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const scriptRef = React.useRef<HTMLScriptElement | null>(null);

  // Add PayPal script to head
  React.useEffect(() => {
    // Remove existing PayPal script if it exists
    const existingScript = document.querySelector('script[data-sdk-integration-source="button-factory"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.src = "https://www.paypal.com/sdk/js?client-id=ATTbHSHbzhfMwdvQ6UsKlADngBjrmbucVJzmKeE5PwpdAWeG5LxkIJP10g0uRqvGUDWiJc4cZsUbTk0T&currency=USD&intent=capture&enable-funding=card&disable-funding=credit,paylater";
    script.async = true;
    script.dataset.sdkIntegrationSource = "button-factory";
    
    document.head.appendChild(script);
    scriptRef.current = script;

    return () => {
      if (scriptRef.current && document.head.contains(scriptRef.current)) {
        document.head.removeChild(scriptRef.current);
      }
    };
  }, []);

  const handleNavigation = (section: string) => {
    if (isHomePage) {
      // If we're on the homepage, just scroll to the section
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // If we're on another page, navigate to homepage with section hash
      navigate(`/#${section}`);
    }
    setIsMobileMenuOpen(false);
  };

  const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
    return (
      <button
        onClick={() => handleNavigation(to)}
        className="nav-link"
      >
        {children}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      {/* Navigation */}
      <nav className="nav-container">
        <div className="nav-content">
          <div className="flex items-center justify-between">
            <RouterLink 
              to="/" 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex-shrink-0"
            >
              <img 
                src="https://qvbmufzjhrhlugblnmwj.supabase.co/storage/v1/object/public/logo//logofinal.png" 
                alt="TopSerah"
                className="nav-logo"
              />
            </RouterLink>
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="nav-menu-button"
            >
              {isMobileMenuOpen ? <X /> : <Menu />}
            </button>

            <div className="nav-links">
              <NavLink to="home">الرئيسية</NavLink>
              <NavLink to="services">خدماتنا</NavLink>
              <NavLink to="testimonials">آراء العملاء</NavLink>
              <NavLink to="pricing">الأسعار</NavLink>
              <NavLink to="faq">الأسئلة الشائعة</NavLink>
              <button
                onClick={() => handleNavigation('pricing')}
                className="nav-cta-button"
              >
                اطلب الآن
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu">
          <NavLink to="home">الرئيسية</NavLink>
          <NavLink to="services">خدماتنا</NavLink>
          <NavLink to="testimonials">آراء العملاء</NavLink>
          <NavLink to="pricing">الأسعار</NavLink>
          <NavLink to="faq">الأسئلة الشائعة</NavLink>
          <button
            onClick={() => handleNavigation('pricing')}
            className="mobile-menu-cta"
          >
            اطلب الآن
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="pb-24">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <h3 className="text-xl font-bold mb-4">تواصل معنا</h3>
              <div className="space-y-2">
                <a href="mailto:info@topserah.com" className="flex items-center gap-2 hover:text-green-400">
                  <Mail className="w-5 h-5" />
                  <span>info@topserah.com</span>
                </a>
                <a href="tel:+966503083277" className="flex items-center gap-2 hover:text-green-400">
                  <Phone className="w-5 h-5" />
                  <span>+966503083277</span>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4">روابط مهمة</h3>
              <div className="space-y-2">
                <button onClick={() => handleNavigation('home')} className="block hover:text-green-400">الرئيسية</button>
                <button onClick={() => handleNavigation('services')} className="block hover:text-green-400">خدماتنا</button>
                <button onClick={() => handleNavigation('testimonials')} className="block hover:text-green-400">آراء العملاء</button>
                <button onClick={() => handleNavigation('pricing')} className="block hover:text-green-400">الأسعار</button>
                <button onClick={() => handleNavigation('faq')} className="block hover:text-green-400">الأسئلة الشائعة</button>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4">تابعنا</h3>
              <div className="flex gap-4">
                {SOCIAL_LINKS.map((link) => {
                  let Icon;
                  switch (link.platform) {
                    case 'facebook':
                      Icon = Facebook;
                      break;
                    case 'x':
                      Icon = Twitter;
                      break;
                    case 'instagram':
                      Icon = Instagram;
                      break;
                    case 'tiktok':
                      Icon = Music2;
                      break;
                    default:
                      Icon = Music2;
                  }
                  
                  return (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-green-600 transition-colors"
                    >
                      <span className="sr-only">{link.platform}</span>
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p>© {new Date().getFullYear()} TopSerah. جميع الحقوق محفوظة</p>
          </div>
        </div>
      </footer>

      <BottomNav />
      <Analytics />
    </div>
  );
}

export default Layout;